/**
 * Cloud authentication — token exchange and refresh.
 *
 * Handles the JWT exchange model: API key → short-lived access token.
 * All HTTP via built-in `fetch` (Node.js 18+). Timeouts via AbortController.
 *
 * SECURITY (Constitution Principle VIII):
 * - API key is never logged or serialized beyond the single POST body.
 * - HTTPS enforced for all non-localhost communication.
 * - Error messages never expose credentials or internal paths.
 */

import { ErrorCode, createError } from '@testrelic/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TokenExchangeResult {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
  readonly orgId: string;
  readonly orgName: string;
  readonly userId: string;
  readonly userName: string;
}

export interface TokenRefreshResult {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
}

export interface AuthError {
  readonly code: 'invalid_key' | 'expired_key' | 'validation_error' | 'rate_limited' | 'server_error' | 'network_error' | 'timeout';
  readonly message: string;
  readonly statusCode: number | null;
}

// ---------------------------------------------------------------------------
// HTTPS Enforcement
// ---------------------------------------------------------------------------

const LOCALHOST_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

/**
 * Validate that the endpoint URL uses HTTPS.
 * HTTP is allowed only for localhost addresses (dev environments).
 * Throws on violation (Constitution Principle VIII — transport security).
 */
export function enforceHttps(endpoint: string): void {
  const url = new URL(endpoint);
  if (url.protocol === 'https:') return;
  if (url.protocol === 'http:' && LOCALHOST_HOSTS.has(url.hostname)) return;
  throw createError(
    ErrorCode.CLOUD_CONFIG_INVALID,
    `HTTPS is required for cloud communication. Got: ${url.protocol}//${url.hostname}`,
  );
}

// ---------------------------------------------------------------------------
// Health Check
// ---------------------------------------------------------------------------

const HEALTH_CHECK_TIMEOUT_MS = 3000;

/**
 * Lightweight connectivity probe — GET request to `{endpoint}/health`.
 * Returns true if the cloud is reachable and healthy, false otherwise.
 */
export async function healthCheck(endpoint: string): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);
  try {
    const response = await fetch(`${endpoint}/health`, {
      method: 'GET',
      signal: controller.signal,
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Structured Error Parsing
// ---------------------------------------------------------------------------

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Parse a structured cloud error response: `{ error: { code, message, details? } }`.
 * Returns the human-readable message, or a fallback string if parsing fails.
 */
async function parseCloudError(response: Response): Promise<string> {
  try {
    const body = await response.json() as Record<string, unknown>;
    const error = body.error as Record<string, unknown> | undefined;
    if (error && typeof error.message === 'string') {
      const details = error.details as Array<{ field: string; message: string }> | undefined;
      if (Array.isArray(details) && details.length > 0) {
        const detailMessages = details.map((d) => `${d.field}: ${d.message}`).join(', ');
        return `${error.message} (${detailMessages})`;
      }
      return error.message;
    }
  } catch {
    // Response body not JSON — use fallback
  }
  return `HTTP ${response.status}`;
}

// ---------------------------------------------------------------------------
// Token Exchange
// ---------------------------------------------------------------------------

/**
 * Exchange an API key for a short-lived JWT access token.
 *
 * POST /sdk/auth/token with { apiKey }
 *
 * Returns a TokenExchangeResult on success.
 * Returns an AuthError on failure (never throws).
 */
export async function exchangeToken(
  endpoint: string,
  apiKey: string,
  timeout: number,
): Promise<TokenExchangeResult | AuthError> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(`${endpoint}/sdk/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiKey }),
      signal: controller.signal,
    });

    if (response.ok) {
      const body = await response.json() as Record<string, unknown>;
      return {
        accessToken: body.accessToken as string,
        refreshToken: body.refreshToken as string,
        expiresIn: body.expiresIn as number,
        orgId: body.orgId as string,
        orgName: body.orgName as string,
        userId: body.userId as string,
        userName: body.userName as string,
      };
    }

    // 429 — rate limited, respect Retry-After and retry once
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 5000;
      if (!isNaN(waitMs) && waitMs > 0) {
        await sleep(waitMs);
        // Retry once after waiting
        const retryResponse = await fetch(`${endpoint}/sdk/auth/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apiKey }),
          signal: controller.signal,
        });
        if (retryResponse.ok) {
          const body = await retryResponse.json() as Record<string, unknown>;
          return {
            accessToken: body.accessToken as string,
            refreshToken: body.refreshToken as string,
            expiresIn: body.expiresIn as number,
            orgId: body.orgId as string,
            orgName: body.orgName as string,
            userId: body.userId as string,
            userName: body.userName as string,
          };
        }
      }
      return { code: 'rate_limited', message: 'Rate limited during token exchange.', statusCode: 429 };
    }

    // 400 — validation error (e.g., malformed API key format)
    if (response.status === 400) {
      const errorMessage = await parseCloudError(response);
      return { code: 'validation_error', message: errorMessage, statusCode: 400 };
    }

    if (response.status === 401) {
      return { code: 'invalid_key', message: 'API key is invalid or has been revoked.', statusCode: 401 };
    }
    if (response.status === 403) {
      return { code: 'expired_key', message: 'API key has expired.', statusCode: 403 };
    }

    const errorMessage = await parseCloudError(response);
    return {
      code: 'server_error',
      message: errorMessage,
      statusCode: response.status,
    };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { code: 'timeout', message: 'Token exchange timed out.', statusCode: null };
    }
    return {
      code: 'network_error',
      message: 'Failed to reach cloud for token exchange.',
      statusCode: null,
    };
  } finally {
    clearTimeout(timer);
  }
}

/** Type guard: check if result is an AuthError. */
export function isAuthError(result: TokenExchangeResult | AuthError): result is AuthError {
  return 'code' in result && typeof (result as AuthError).code === 'string'
    && ['invalid_key', 'expired_key', 'validation_error', 'rate_limited', 'server_error', 'network_error', 'timeout'].includes((result as AuthError).code);
}

// ---------------------------------------------------------------------------
// Token Refresh
// ---------------------------------------------------------------------------

/**
 * Refresh an expiring access token.
 *
 * POST /sdk/auth/refresh with { refreshToken }
 *
 * Returns a TokenRefreshResult on success, or null on failure.
 */
export async function refreshAccessToken(
  endpoint: string,
  currentRefreshToken: string,
  timeout: number,
): Promise<TokenRefreshResult | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    let response = await fetch(`${endpoint}/sdk/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
      signal: controller.signal,
    });

    // 429 — rate limited, respect Retry-After and retry once
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After');
      const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 5000;
      if (!isNaN(waitMs) && waitMs > 0) {
        await sleep(waitMs);
        response = await fetch(`${endpoint}/sdk/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: currentRefreshToken }),
          signal: controller.signal,
        });
      }
    }

    if (!response.ok) return null;

    const body = await response.json() as Record<string, unknown>;
    return {
      accessToken: body.accessToken as string,
      refreshToken: body.refreshToken as string,
      expiresIn: body.expiresIn as number,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// ---------------------------------------------------------------------------
// Repo Resolution
// ---------------------------------------------------------------------------

export interface RepoResolveResult {
  readonly repoId: string;
  readonly displayName: string;
}

/**
 * Resolve or auto-create a repo by git identity.
 *
 * POST /repos/resolve with { gitId, displayName, branch }
 *
 * Returns the resolved repo, or null on failure.
 */
export async function resolveRepo(
  endpoint: string,
  accessToken: string,
  gitId: string,
  displayName: string,
  timeout: number,
  branch?: string | null,
): Promise<RepoResolveResult | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    const requestBody: Record<string, string> = { gitId, displayName };
    if (branch) requestBody.branch = branch;
    const response = await fetch(`${endpoint}/repos/resolve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal,
    });

    if (!response.ok) return null;

    const responseBody = await response.json() as Record<string, unknown>;
    return {
      repoId: responseBody.repoId as string,
      displayName: responseBody.displayName as string,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
